import * as Sentry from "@sentry/react";
import jwtDecode from "jwt-decode";
import { NavigateFunction } from "react-router-dom";
import { Action } from "redux";

import {
  PWTokenObtainPair,
  PasswordChange,
  PasswordReset,
  PasswordResetConfirm,
  User,
  MFALoginSend,
  MFASend,
  ApiService,
} from "pwapi";

import { JwtResponse } from "custom-models";

import {
  LOGIN_REQUEST,
  TOKEN_REQUEST,
  TOKEN_PAIR_REQUEST,
  DEAUTHENTICATE,
  WIPE_PW_RESET,
  PW_RESET_REQUEST,
  WIPE_PW_RESET_CONFIRM,
  PW_RESET_CONFIRM_REQUEST,
  USER_DETAIL_REQUEST,
  USER_ME_REQUEST,
  USER_ME_SUCCESS,
  USER_ME_FAILURE,
  SAVE_PROFILE_REQUEST,
  SET_PROFILE_RESET_ERRORS,
  LOGIN_BARCODE,
  RESET_AUTH_ERRORS,
  MFA_GET_CODE_REQUEST,
  MFA_VERIFY_CODE_REQUEST,
  MFA_SELECT_METHOD,
  MFA_LOGIN_GET_CODE_REQUEST,
  USER_UNSET_MFA_METHOD_REQUEST,
  USER_UNSET_MFA_METHOD_FAILURE,
  USER_UNSET_MFA_METHOD_SUCCESS,
  RESET_AUTH_STATE,
  LOGIN_SUCCESS,
  TOKEN_RECEIVED,
} from "./constants";
import { AuthState } from "../reducers/authReducer";
import { Jwt } from "../reducers/authReducer";
import {
  createAsyncSaga,
  AsyncSagaActionNoArgs,
  AsyncSagaAction,
} from "../utils/asyncActions";
import {
  isVersionOutdated,
  setCookieAndOpenAPI,
  unsetCookieAndOpenAPI,
} from "../utils/authUtils";

export type ConfirmResetPasswordAction = Action<string> & {
  payload: {
    newPasswordDetails: PasswordResetConfirm;
    navigate: NavigateFunction;
  };
};

export const getLocationUrl = (loc: Location) => {
  return `${loc.host}${loc.pathname}${loc.search ?? ""}${loc.hash ?? ""}`;
};

export const new_login = (
  loginDetails:
    | (PWTokenObtainPair & { url?: string })
    | { is_sso: boolean; url?: string }
) => ({
  type: LOGIN_REQUEST,
  payload: loginDetails,
});

export const wipe_password_reset = () => ({
  type: WIPE_PW_RESET,
});

export const wipe_password_confirm_reset = () => ({
  type: WIPE_PW_RESET_CONFIRM,
});

export const reset_password = (passwordReset: PasswordReset) => ({
  type: PW_RESET_REQUEST,
  payload: passwordReset,
});

export const confirm_reset_password = (
  newPasswordDetails: PasswordResetConfirm,
  navigate: NavigateFunction
): ConfirmResetPasswordAction => {
  return {
    type: PW_RESET_CONFIRM_REQUEST,
    payload: { newPasswordDetails, navigate },
  };
};

export const saveProfile = (
  user: User,
  passwordChange: PasswordChange,
  onSuccess?: Function
) => ({
  type: SAVE_PROFILE_REQUEST,
  payload: {
    user,
    passwordChange,
    onSuccess,
  },
  requestKey: "user/me/update",
});

export const resetSaveProfileErrors = (resetKeys?: string | [string] | {}) => ({
  type: SET_PROFILE_RESET_ERRORS,
  payload: resetKeys,
  requestKey: "user/me/update",
  requestResetErrors: true,
});

export const refreshAccessToken = () => ({ type: TOKEN_REQUEST });
export const refreshAccessTokenPair = () => ({ type: TOKEN_PAIR_REQUEST });

export const userMe = createAsyncSaga(
  USER_ME_REQUEST,
  USER_ME_SUCCESS,
  USER_ME_FAILURE,
  () => `user/me`,
  ApiService.meUser,
  {
    retries: 2,
    retryDelay: 2000,
  }
) as AsyncSagaActionNoArgs;

export const userUnsetMFA = createAsyncSaga(
  USER_UNSET_MFA_METHOD_REQUEST,
  USER_UNSET_MFA_METHOD_SUCCESS,
  USER_UNSET_MFA_METHOD_FAILURE,
  (id: number) => `user/${id}/mfa/unset`,
  (id: number) => ApiService.unsetMfaMethodUser({ id })
) as AsyncSagaAction<number>;

export const userDetail = (id: number) => ({
  type: USER_DETAIL_REQUEST,
  payload: { id },
});

export const logout = () => {
  unsetCookieAndOpenAPI();

  return { type: DEAUTHENTICATE };
};

// resetAuth() resets the auth state just like logout() without any other functionality.
// It is used to exit from the MFA token required flow.
export const resetAuth = () => ({ type: RESET_AUTH_STATE });

export const barcodeLogin = (token: string, pin: string) => ({
  type: LOGIN_BARCODE,
  payload: { token, pin },
});

export const resetErrors = () => ({ type: RESET_AUTH_ERRORS });

export const mfaLoginGetCode = (
  mfaMethod: MFALoginSend.mfa_method,
  username: string
) => ({
  type: MFA_LOGIN_GET_CODE_REQUEST,
  payload: { mfaMethod, username },
  requestKey: "mfa-get-code",
});

export const mfaGetCode = (mfaMethod: MFASend.mfa_method) => ({
  type: MFA_GET_CODE_REQUEST,
  payload: { mfaMethod },
  requestKey: "mfa-get-code",
});

export const mfaVerifyCode = (
  mfaMethod: MFASend.mfa_method,
  mfaCode: string
) => ({
  type: MFA_VERIFY_CODE_REQUEST,
  payload: { mfaMethod, mfaCode },
  requestKey: "mfa-verify-code",
});

export const mfaSelectMethod = (mfaMethod: undefined | MFASend.mfa_method) => ({
  type: MFA_SELECT_METHOD,
  payload: mfaMethod,
});

export function loginSuccess(response: JwtResponse) {
  // If there are no access/refresh tokens AND no federated login, fail
  if ((!response.access || !response.refresh) && !response.federated_login) {
    throw new Error("loginSuccess() called without access and refresh tokens");
  }

  // If this is a federated login, redirect instead of decoding JWTs
  if (response.federated_login && (!response.access || !response.refresh)) {
    window.location.href = response.federated_login;
    return { type: "redirecting-to-idp", payload: {} };
  }

  // Decode JWTs (safe now because we have tokens)
  const access: Jwt = jwtDecode(response.access!);
  const refresh: Jwt = jwtDecode(response.refresh!);

  setCookieAndOpenAPI(response);

  if (isVersionOutdated(response.versions)) {
    Sentry.captureMessage("Automatically reloading at login for new version");
    window.location.reload();
    return { type: "no-op", payload: {} };
  }

  return {
    type: LOGIN_SUCCESS,
    payload: {
      access,
      refresh,
      mfa_setup_required: !!response.mfa_setup_required,
      has_phone: !!response.has_phone,
      has_email: !!response.has_email,
    },
  };
}

export function tokenRetrieved(response: JwtResponse) {
  if (!response.access && !response.refresh)
    throw new Error("tokenRetrieved() called without access or refresh tokens");

  const payload: Partial<AuthState> = {};

  if (response.access) payload.access = jwtDecode(response.access);
  if (response.refresh) payload.refresh = jwtDecode(response.refresh);
  if (response.versions) payload.versions = response.versions;

  setCookieAndOpenAPI(response);

  return { type: TOKEN_RECEIVED, payload };
}
